<script setup>
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import PageLayout from 'dashboard/components-next/ailab/PageLayout.vue';
import EditAssistantForm from '../../../../components-next/ailab/pageComponents/assistant/EditAssistantForm.vue';
import AssistantPlayground from 'dashboard/components-next/ailab/assistant/AssistantPlayground.vue';

import AssistantSettings from 'dashboard/routes/dashboard/ailab/assistants/settings/Settings.vue';

const route = useRoute();
const store = useStore();
const { t } = useI18n();
const assistantId = route.params.assistantId;
const uiFlags = useMapGetter('ailabAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['ailabAssistants/getRecord'](Number(assistantId))
);

const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const currentAccountId = useMapGetter('getCurrentAccountId');

const isAilabV2Enabled = isFeatureEnabledonAccount.value(
  currentAccountId.value,
  FEATURE_FLAGS.CAPTAIN_V2
);

const isAssistantAvailable = computed(() => !!assistant.value?.id);

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('ailabAssistants/update', {
      id: assistantId,
      ...updatedAssistant,
    });
    useAlert(t('AILAB.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('AILAB.ASSISTANTS.EDIT.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

onMounted(() => {
  if (!isAssistantAvailable.value || !isAilabV2Enabled) {
    store.dispatch('ailabAssistants/show', assistantId);
  }
});
</script>

<template>
  <AssistantSettings v-if="isAilabV2Enabled" />
  <PageLayout
    v-else
    :header-title="assistant?.name"
    :show-pagination-footer="false"
    :is-fetching="isFetching"
    :show-know-more="false"
    :back-url="{ name: 'ailab_assistants_index' }"
  >
    <template #headerIcon>
      <div
        class="w-11 h-11 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-900 flex-shrink-0"
      >
        <span class="i-lucide-bot size-5" />
      </div>
    </template>

    <template #headerSubtitle>
      <div class="text-sm text-slate-600">
        Configure identity and test conversations in real time.
      </div>
    </template>

    <template #body>
      <div v-if="!isAssistantAvailable">
        {{ t('AILAB.ASSISTANTS.EDIT.NOT_FOUND') }}
      </div>
      <div v-else class="flex gap-4 h-full">
        <div class="flex-1 lg:overflow-auto pr-4 h-full md:h-auto">
          <EditAssistantForm
            :assistant="assistant"
            mode="edit"
            @submit="handleSubmit"
          />
        </div>
        <div class="w-[400px] hidden lg:block h-full">
          <AssistantPlayground :assistant-id="Number(assistantId)" />
        </div>
      </div>
    </template>
  </PageLayout>
</template>
